import { Button } from "@/components/ui/button";
import { Leaf } from "lucide-react";
import { useState, useEffect, lazy, Suspense } from "react";

const ContactModal = lazy(() => import("./ContactModal"));

interface HeaderProps {
  onContactClick?: () => void;
  contactModalOpen?: boolean;
  onContactModalChange?: (open: boolean) => void;
}

const Header = ({ onContactClick, contactModalOpen, onContactModalChange }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [localModalOpen, setLocalModalOpen] = useState(false);

  const isControlled = contactModalOpen !== undefined && onContactModalChange !== undefined;
  const modalOpen = isControlled ? contactModalOpen : localModalOpen;
  const setModalOpen = isControlled ? onContactModalChange : setLocalModalOpen;

  const handleContactClick = () => {
    setMobileMenuOpen(false);
    if (onContactClick) {
      onContactClick();
    } else {
      setModalOpen(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on significant scroll (more than 100px from when menu opened)
  useEffect(() => {
    if (mobileMenuOpen) {
      setScrollPosition(window.scrollY);
      
      const handleScroll = () => {
        if (Math.abs(window.scrollY - scrollPosition) > 100) {
          setMobileMenuOpen(false);
        }
      };
      
      // Add listener after a short delay to prevent immediate closing
      const timeoutId = setTimeout(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [mobileMenuOpen, scrollPosition]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const navLinks = [
    { href: "#funcionalidades", label: "Funcionalidades" },
    { href: "#como-funciona", label: "Como Funciona" },
    { href: "#ecossistema", label: "Ecossistema" },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
          scrolled 
            ? 'bg-background/60 backdrop-blur-xl border-border/50 shadow-sm' 
            : 'bg-background/80 backdrop-blur-md border-transparent'
        }`}
      >
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 cursor-pointer z-50"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-primary" />
            </div>
            <span className="text-xl font-semibold text-foreground">Nutri IA</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.href}
                href={link.href} 
                onClick={(e) => handleSmoothScroll(e, link.href)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Button size="default" className="hidden md:inline-flex" onClick={handleContactClick}>
              Entre em contato
            </Button>

            {/* Hamburger Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden relative w-10 h-10 flex items-center justify-center focus:outline-none z-50"
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span 
                  className={`w-full h-0.5 bg-foreground rounded-full transition-all duration-300 origin-center ${
                    mobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                  }`}
                />
                <span 
                  className={`w-full h-0.5 bg-foreground rounded-full transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                  }`}
                />
                <span 
                  className={`w-full h-0.5 bg-foreground rounded-full transition-all duration-300 origin-center ${
                    mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div 
        className={`fixed top-16 left-0 right-0 bg-background border-b border-border z-40 md:hidden transition-all duration-300 ease-out ${
          mobileMenuOpen 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="container mx-auto px-6 py-6">
          <ul className="space-y-1">
            {navLinks.map((link, index) => (
              <li 
                key={link.href}
                className="overflow-hidden"
                style={{ 
                  transitionDelay: mobileMenuOpen ? `${index * 50}ms` : '0ms' 
                }}
              >
                <a 
                  href={link.href} 
                  onClick={(e) => handleSmoothScroll(e, link.href)}
                  className={`block py-3 px-4 text-lg font-medium text-foreground hover:bg-muted rounded-lg transition-all duration-300 ${
                    mobileMenuOpen 
                      ? 'translate-x-0 opacity-100' 
                      : '-translate-x-4 opacity-0'
                  }`}
                  style={{ 
                    transitionDelay: mobileMenuOpen ? `${index * 50 + 100}ms` : '0ms' 
                  }}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          
          <div 
            className={`mt-6 pt-6 border-t border-border transition-all duration-300 ${
              mobileMenuOpen 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-4 opacity-0'
            }`}
            style={{ 
              transitionDelay: mobileMenuOpen ? `${navLinks.length * 50 + 150}ms` : '0ms' 
            }}
          >
            <Button size="lg" className="w-full" onClick={handleContactClick}>
              Entre em contato
            </Button>
          </div>
        </nav>
      </div>

      {modalOpen && (
        <Suspense fallback={null}>
          <ContactModal open={modalOpen} onOpenChange={setModalOpen} />
        </Suspense>
      )}
    </>
  );
};

export default Header;
